import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ContactForm from '../components/Resources/ContactForm';
import FuneralPlanning from '../components/Resources/FuneralPlanning';
import LegalWillPrep from '../components/Resources/LegalWillPrep';
import WorkshopsGrid from '../components/Resources/Workshops/WorkshopsGrid';
import WorkshopCalendar from '../components/Resources/Workshops/WorkshopCalendar';
import JoinWorkshopModal from '../components/Resources/Workshops/JoinWorkshopModal';
import EventReminderModal from '../components/Modals/EventReminderModal';
import SupportGroupCard from '../components/Resources/SupportGroups/SupportGroupCard';
import CreateGroupModal from '../components/Resources/SupportGroups/CreateGroupModal';
import GroupDetailView from '../components/Resources/SupportGroups/GroupDetailView';
import ObituaryHelperModal from '../components/Modals/ObituaryHelperModal';
import AdminResourceEditor from '../components/Resources/AdminResourceEditor';
import ShareModal from '../components/Modals/ShareModal';
import { useAuth } from '../context/AuthContext';
import {
  fetchArticles,
  fetchHelpArticles,
  fetchFeaturedVideos,
  incrementArticleView,
  incrementVideoView,
  incrementFeaturedVideoView,
} from '../services/resourcesEditorService';
import { mockResources } from '../data/mockData';
import { mockWorkshops } from '../data/workshopData';
import { mockSupportGroups } from '../data/supportGroupData';

const {
  FiHeart, FiArrowRight, FiShield, FiZap, FiPlus, FiUsers,
  FiSearch, FiFeather, FiAward, FiFileText, FiBriefcase,
  FiCheckCircle, FiLock, FiMail, FiUser, FiEye, FiEdit3,
  FiShoppingBag, FiKey, FiBookOpen, FiVideo, FiSettings, FiStar,
  FiPlay, FiShare2, FiTrendingUp,
} = FiIcons;

const CATEGORIES = [
  'All', 'Articles', 'Support Groups', 'Workshops',
  'Funeral Planning', 'Legal & Will Prep', 'Estate Services',
  'User Roles', 'Legal', 'About',
];
const GRID_COLS_KEY = 'resources_grid_cols';

// ── helpers ───────────────────────────────────────────────────────────────────
const youtubeThumb = (url) => {
  const m = url?.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
};

const buildShareUrl = (item) => {
  const base = window.location.origin + window.location.pathname.replace(/\/[^/]*$/, '');
  if (item?.slug) return `${base}/resources/${item.slug}`;
  if (item?.id)   return `${base}/resources/${item.id}`;
  return window.location.href;
};

const fmtViews = (n) => {
  const v = parseInt(n, 10) || 0;
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000)    return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

// ── View Count Badge ──────────────────────────────────────────────────────────
const ViewBadge = ({ count, className = '' }) => (
  <span className={`inline-flex items-center gap-1 text-[9px] font-bold text-text-muted
                    uppercase tracking-widest ${className}`}>
    <SafeIcon icon={FiEye} className="w-3 h-3" />
    {fmtViews(count)}
  </span>
);

// ── Share Button ──────────────────────────────────────────────────────────────
const ShareBtn = ({ onClick, className = '' }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    title="Share"
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-brand-dark bg-white/90
                text-[9px] font-black uppercase tracking-widest text-text-muted
                hover:bg-accent-navy hover:text-white hover:border-accent-navy
                transition-all backdrop-blur-sm ${className}`}
  >
    <SafeIcon icon={FiShare2} className="w-3 h-3" />
    Share
  </button>
);

// ── Featured Article Card ─────────────────────────────────────────────────────
const FeaturedArticleCard = ({ article, index, onShare }) => {
  const fired = useRef(false);
  useEffect(() => {
    if (!fired.current && article?.id) {
      fired.current = true;
      incrementArticleView(article.id, article.view_count);
    }
  }, [article]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08 }}
      className="group flex flex-col sm:flex-row bg-white rounded-[32px] border border-brand-dark
                 shadow-sm hover:shadow-xl transition-all overflow-hidden"
    >
      {/* Thumbnail */}
      <div className="sm:w-64 lg:w-80 flex-shrink-0 h-48 sm:h-auto relative overflow-hidden
                      bg-gradient-to-br from-accent-navy/10 to-accent-gold/10">
        {article.cover_image_url ? (
          <img src={article.cover_image_url} alt={article.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <SafeIcon icon={FiFileText} className="w-16 h-16 text-accent-navy/20" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-accent-gold text-accent-navy
                        px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shadow-md">
          <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /> Featured
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 md:p-8 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="bg-accent-navy/5 text-accent-navy px-3 py-1 rounded-full
                             text-[9px] font-black uppercase tracking-widest">
              {article.category}
            </span>
            {article.youtube_url && (
              <span className="flex items-center gap-1 text-[9px] text-red-500 font-bold uppercase tracking-widest">
                <SafeIcon icon={FiVideo} className="w-3 h-3" /> Video
              </span>
            )}
            <ViewBadge count={article.view_count} />
          </div>
          <h3 className="text-xl md:text-2xl font-serif text-accent-navy mb-3
                         group-hover:text-accent-gold transition-colors line-clamp-2">
            {article.title}
          </h3>
          {(article.excerpt || article.description) && (
            <p className="text-sm text-text-muted font-light leading-relaxed line-clamp-3">
              {article.excerpt || article.description}
            </p>
          )}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest
                             text-accent-gold hover:gap-3 transition-all">
            Read Article <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5" />
          </button>
          <ShareBtn onClick={() => onShare(article, 'article')} />
        </div>
      </div>
    </motion.div>
  );
};

// ── Featured Video Card ───────────────────────────────────────────────────────
const FeaturedVideoCard = ({ video, index, onShare }) => {
  const [playing, setPlaying] = useState(false);
  const fired = useRef(false);
  const thumb   = youtubeThumb(video.youtube_url);
  const videoId = video.youtube_url?.match(/(?:v=|youtu\.be\/)([^&?/]+)/)?.[1];

  const handlePlay = () => {
    setPlaying(true);
    if (!fired.current && video?.id) {
      fired.current = true;
      incrementFeaturedVideoView(video.id, video.view_count);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08 }}
      className="group flex flex-col sm:flex-row bg-white rounded-[32px] border border-brand-dark
                 shadow-sm hover:shadow-xl transition-all overflow-hidden"
    >
      {/* Thumbnail / Player */}
      <div className="sm:w-64 lg:w-80 flex-shrink-0 h-48 sm:h-auto relative overflow-hidden bg-black">
        {playing && videoId ? (
          <iframe src={`https://www.youtube.com/embed/${videoId}?autoplay=1`} title={video.title}
            allow="autoplay; fullscreen" className="w-full h-full border-0" />
        ) : (
          <>
            {thumb ? (
              <img src={thumb} alt={video.title}
                className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <SafeIcon icon={FiVideo} className="w-16 h-16 text-white/20" />
              </div>
            )}
            <button onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center
                              shadow-xl hover:scale-110 transition-transform">
                <SafeIcon icon={FiPlay} className="w-6 h-6 text-accent-navy ml-1" />
              </div>
            </button>
            <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-accent-gold text-accent-navy
                            px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shadow-md">
              <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /> Featured
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 p-6 md:p-8 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="flex items-center gap-1 text-[9px] text-red-500 font-bold uppercase
                             tracking-widest bg-red-50 px-3 py-1 rounded-full">
              <SafeIcon icon={FiVideo} className="w-3 h-3" /> Video
            </span>
            {video.category && (
              <span className="bg-accent-navy/5 text-accent-navy px-3 py-1 rounded-full
                               text-[9px] font-black uppercase tracking-widest">
                {video.category}
              </span>
            )}
            <ViewBadge count={video.view_count} />
          </div>
          <h3 className="text-xl md:text-2xl font-serif text-accent-navy mb-3
                         group-hover:text-accent-gold transition-colors line-clamp-2">
            {video.title}
          </h3>
          {(video.description || video.content) && (
            <p className="text-sm text-text-muted font-light leading-relaxed line-clamp-3">
              {video.description || video.content}
            </p>
          )}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button onClick={handlePlay}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest
                       text-red-500 hover:gap-3 transition-all">
            Watch Now <SafeIcon icon={FiPlay} className="w-3.5 h-3.5" />
          </button>
          <ShareBtn onClick={() => onShare(video, 'video')} />
        </div>
      </div>
    </motion.div>
  );
};

// ── Spotlight Section ─────────────────────────────────────────────────────────
const SpotlightSection = ({ featuredArticles, featuredVideos, loading, onShare }) => {
  const hasContent = featuredArticles.length > 0 || featuredVideos.length > 0;

  if (loading) {
    return (
      <div className="space-y-4 mb-12">
        <div className="h-5 w-40 bg-brand-dark/30 rounded-full animate-pulse" />
        {[0, 1].map(i => (
          <div key={i} className="h-44 bg-white rounded-[32px] border border-brand-dark animate-pulse" />
        ))}
      </div>
    );
  }
  if (!hasContent) return null;

  const allSpotlight = [
    ...featuredArticles.map(a => ({ ...a, _type: 'article' })),
    ...featuredVideos.map(v => ({ ...v, _type: 'video' })),
  ].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-14">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-accent-gold rounded-xl flex items-center justify-center flex-shrink-0">
          <SafeIcon icon={FiStar} className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-serif text-accent-navy leading-tight">Featured Spotlight</h2>
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
            Curated articles &amp; videos
          </p>
        </div>
        <div className="flex-1 h-px bg-brand-dark ml-2" />
      </div>
      <div className="flex flex-col gap-5">
        {allSpotlight.map((item, idx) =>
          item._type === 'article' ? (
            <FeaturedArticleCard key={`fa-${item.id}`} article={item} index={idx} onShare={onShare} />
          ) : (
            <FeaturedVideoCard key={`fv-${item.id}`} video={item} index={idx} onShare={onShare} />
          )
        )}
      </div>
      <div className="mt-12 flex items-center gap-4">
        <div className="flex-1 h-px bg-brand-dark" />
        <span className="text-[9px] font-black uppercase tracking-[0.25em] text-text-muted px-3">
          All Resources
        </span>
        <div className="flex-1 h-px bg-brand-dark" />
      </div>
    </motion.div>
  );
};

// ── Article Card ──────────────────────────────────────────────────────────────
const ArticleCard = ({ resource, index, isAdmin, onEdit, onShare, onView }) => {
  const fired = useRef(false);

  const handleRead = () => {
    if (!fired.current && resource?.id) {
      fired.current = true;
      onView(resource);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07 }}
      className="bg-white rounded-[32px] md:rounded-[40px] overflow-hidden border border-brand-dark
                 group shadow-sm hover:shadow-xl transition-all relative"
    >
      {isAdmin && (
        <button onClick={onEdit}
          className="absolute top-4 right-4 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-xl
                     border border-brand-dark opacity-0 group-hover:opacity-100 transition-opacity
                     hover:bg-accent-navy hover:text-white hover:border-accent-navy"
          title="Edit in editor">
          <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Cover */}
      <div className="h-56 md:h-64 overflow-hidden relative">
        {resource.cover_image_url || resource.image ? (
          <img src={resource.cover_image_url || resource.image}
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
            alt={resource.title} />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-accent-navy/10 to-accent-gold/10
                          flex items-center justify-center">
            <SafeIcon icon={FiFileText} className="w-16 h-16 text-accent-navy/20" />
          </div>
        )}
        <div className="absolute top-4 md:top-6 left-4 md:left-6 bg-white/90 px-3 md:px-4 py-1
                        md:py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest text-accent-navy">
          {resource.category}
        </div>
        {(resource.is_featured == 1 || resource.is_featured === true) && (
          <div className="absolute top-4 right-4 bg-accent-gold px-2 py-1 rounded-full">
            <SafeIcon icon={FiStar} className="w-3 h-3 text-white" />
          </div>
        )}
        {/* View count overlay */}
        {(parseInt(resource.view_count, 10) > 0) && (
          <div className="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm
                          text-white px-2 py-1 rounded-lg flex items-center gap-1">
            <SafeIcon icon={FiEye} className="w-3 h-3" />
            <span className="text-[9px] font-bold">{fmtViews(resource.view_count)}</span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-6 md:p-10">
        <h3 className="text-xl md:text-2xl font-serif text-accent-navy mb-3 md:mb-4
                       group-hover:text-accent-gold transition-colors line-clamp-2">
          {resource.title}
        </h3>
        {(resource.excerpt || resource.description) && (
          <p className="text-sm text-text-muted font-light leading-relaxed mb-4 line-clamp-2">
            {resource.excerpt || resource.description}
          </p>
        )}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={handleRead}
              className="flex items-center gap-2 text-[10px] font-black uppercase
                         tracking-widest text-accent-gold transition-all hover:gap-3">
              Read More <SafeIcon icon={FiArrowRight} />
            </button>
            {resource.youtube_url && (
              <span className="flex items-center gap-1 text-[9px] text-red-500 font-bold uppercase tracking-widest">
                <SafeIcon icon={FiVideo} className="w-3 h-3" /> Video
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ViewBadge count={resource.view_count} />
            <ShareBtn onClick={() => onShare(resource, 'article')} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ── Help Video Card ───────────────────────────────────────────────────────────
const HelpVideoCard = ({ video, index, onShare }) => {
  const thumb   = youtubeThumb(video.youtube_url || video.url);
  const videoId = (video.youtube_url || video.url)?.match(/(?:v=|youtu\.be\/)([^&?/]+)/)?.[1];
  const [playing, setPlaying] = useState(false);
  const fired = useRef(false);

  const handlePlay = () => {
    setPlaying(true);
    if (!fired.current && video?.id && !video.id.startsWith('hv')) {
      fired.current = true;
      incrementVideoView(video.id, video.view_count);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="group bg-white rounded-[28px] border border-brand-dark overflow-hidden
                 shadow-sm hover:shadow-xl transition-all"
    >
      {/* Thumbnail */}
      <div className="relative h-48 bg-black overflow-hidden">
        {playing && videoId ? (
          <iframe src={`https://www.youtube.com/embed/${videoId}?autoplay=1`} title={video.title}
            allow="autoplay; fullscreen" className="w-full h-full border-0" />
        ) : (
          <>
            {thumb ? (
              <img src={thumb} alt={video.title}
                className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <SafeIcon icon={FiVideo} className="w-12 h-12 text-white/20" />
              </div>
            )}
            <button onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center
                              shadow-lg hover:scale-110 transition-transform">
                <SafeIcon icon={FiPlay} className="w-5 h-5 text-accent-navy ml-0.5" />
              </div>
            </button>
            {/* View count on thumbnail */}
            {(parseInt(video.view_count, 10) > 0) && (
              <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-0.5
                              rounded-md flex items-center gap-1">
                <SafeIcon icon={FiEye} className="w-2.5 h-2.5" />
                <span className="text-[8px] font-bold">{fmtViews(video.view_count)}</span>
              </div>
            )}
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <ShareBtn onClick={() => onShare(video, 'video')} />
            </div>
          </>
        )}
      </div>

      {/* Info */}
      <div className="p-5">
        {video.category && (
          <span className="inline-block bg-accent-navy/5 text-accent-navy px-3 py-1 rounded-full
                           text-[9px] font-black uppercase tracking-widest mb-2">
            {video.category}
          </span>
        )}
        <h4 className="font-serif text-accent-navy text-base leading-snug mb-1 line-clamp-2
                       group-hover:text-accent-gold transition-colors">
          {video.title}
        </h4>
        {(video.content || video.description) && (
          <p className="text-xs text-text-muted font-light leading-relaxed line-clamp-2 mt-1">
            {video.content || video.description}
          </p>
        )}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={handlePlay}
              className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest
                         text-red-500 hover:gap-2.5 transition-all">
              <SafeIcon icon={FiPlay} className="w-3 h-3" /> Watch
            </button>
            <ViewBadge count={video.view_count} />
          </div>
          <ShareBtn onClick={() => onShare(video, 'video')} className="opacity-0 group-hover:opacity-100" />
        </div>
      </div>
    </motion.div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const Resources = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useAuth();

  const isAdmin = user && (
    permissions?.isGlobalAdmin?.() ||
    permissions?.isSuperAdmin?.()
  );

  const getTabFromSearch = () => {
    const params = new URLSearchParams(location.search);
    const tab    = params.get('tab');
    return tab && CATEGORIES.includes(tab) ? tab : 'All';
  };

  const [gridCols, setGridCols] = useState(() => {
    const saved  = localStorage.getItem(GRID_COLS_KEY);
    const parsed = parseInt(saved, 10);
    return [1, 2, 3, 4].includes(parsed) ? parsed : 2;
  });

  const persistGridCols = (n) => {
    setGridCols(n);
    localStorage.setItem(GRID_COLS_KEY, String(n));
  };

  const [activeCategory,    setActiveCategory]    = useState(getTabFromSearch);
  const [selectedWorkshop,  setSelectedWorkshop]  = useState(null);
  const [reminderWorkshop,  setReminderWorkshop]  = useState(null);
  const [selectedGroup,     setSelectedGroup]     = useState(null);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [isObituaryOpen,    setIsObituaryOpen]    = useState(false);
  const [groups,            setGroups]            = useState(mockSupportGroups);
  const [joinedGroups,      setJoinedGroups]      = useState(['g1']);
  const [isEditorOpen,      setIsEditorOpen]      = useState(false);

  // ── Share state ──────────────────────────────────────────────────────────────
  const [shareTarget, setShareTarget] = useState(null);
  const openShare  = useCallback((item, type) => setShareTarget({ item, type }), []);
  const closeShare = useCallback(() => setShareTarget(null), []);

  // ── Content state ─────────────────────────────────────────────────────────────
  const [dbArticles,       setDbArticles]       = useState([]);
  const [dbVideos,         setDbVideos]         = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [featuredVideos,   setFeaturedVideos]   = useState([]);
  const [loadingContent,   setLoadingContent]   = useState(true);

  // Optimistic view-count updater (avoids re-fetch for article cards)
  const [localViewCounts, setLocalViewCounts] = useState({});

  const handleArticleView = useCallback((resource) => {
    if (!resource?.id || resource.id.startsWith('mock')) return;
    incrementArticleView(resource.id, resource.view_count);
    setLocalViewCounts(prev => ({
      ...prev,
      [resource.id]: (parseInt(resource.view_count, 10) || 0) + 1,
    }));
  }, []);

  useEffect(() => {
    const tab = getTabFromSearch();
    setActiveCategory(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.search]);

  const loadContent = useCallback(async () => {
    setLoadingContent(true);
    try {
      const [arts, vids, featVids] = await Promise.all([
        fetchArticles(),
        fetchHelpArticles(),
        fetchFeaturedVideos(),
      ]);
      const publishedArts = arts.filter(a => !a.status || a.status === 'published');
      setDbArticles(publishedArts);
      setFeaturedArticles(publishedArts.filter(a => a.is_featured == 1 || a.is_featured === true));
      setDbVideos(vids.filter(v => v.is_published));
      setFeaturedVideos(featVids.filter(v => v.is_active == 1 || v.is_active === true));
    } catch { /* silent */ }
    setLoadingContent(false);
  }, []);

  useEffect(() => { loadContent(); }, [loadContent]);

  const handleEditorClose = () => { setIsEditorOpen(false); loadContent(); };
  const handleTabChange   = (cat) => {
    setActiveCategory(cat);
    navigate(`/resources?tab=${encodeURIComponent(cat)}`);
  };
  const handleJoinGroup   = (id) => {
    if (!joinedGroups.includes(id)) setJoinedGroups([...joinedGroups, id]);
    setSelectedGroup(groups.find(g => g.id === id));
  };
  const handleLeaveGroup  = (id) => setJoinedGroups(joinedGroups.filter(g => g !== id));
  const handleCreateGroup = (g)  => setGroups([g, ...groups]);

  // Merge optimistic counts into articles
  const allArticles = (dbArticles.length > 0 ? dbArticles : mockResources).map(a => ({
    ...a,
    view_count: localViewCounts[a.id] ?? a.view_count,
  }));
  const filteredResources = allArticles.filter(
    res => activeCategory === 'All' || res.category === activeCategory
  );

  const helpVideos = dbVideos.length > 0 ? dbVideos : [
    { id: 'hv1', youtube_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      title: 'Getting Started with OneFinalLegacy',
      content: 'A quick walkthrough of creating your first memorial page and inviting family members.',
      category: 'Platform Tutorial', view_count: 0 },
    { id: 'hv2', youtube_url: 'https://www.youtube.com/watch?v=ScMzIvxBSi4',
      title: 'Understanding Grief — A Compassionate Guide',
      content: 'Mental health professionals share evidence-based strategies for navigating loss.',
      category: 'Grief Support', view_count: 0 },
    { id: 'hv3', youtube_url: 'https://www.youtube.com/watch?v=9bZkp7q19f0',
      title: 'Digital Legacy Planning: What You Need to Know',
      content: 'How to organise your digital assets, designate heirs, and protect your online presence.',
      category: 'Estate Planning', view_count: 0 },
  ];

  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }[gridCols];

  const showSpotlight = activeCategory === 'All' || activeCategory === 'Articles';

  // Share modal derived props
  const shareItem  = shareTarget?.item;
  const shareType  = shareTarget?.type;
  const shareTitle = shareItem
    ? shareType === 'video' ? `🎬 ${shareItem.title} — OneFinalLegacy`
                            : `📖 ${shareItem.title} — OneFinalLegacy`
    : '';
  const shareDesc  = shareItem?.excerpt || shareItem?.description || shareItem?.content || '';
  const shareUrl   = shareItem ? buildShareUrl(shareItem) : '';

  const userRoles = [
    { role: 'Visitor',         icon: FiEye,         color: 'blue',
      description: 'Any unauthenticated user browsing the sanctuary.',
      permissions: ['View public memorial pages','Browse the marketplace','Read public resources and articles','View live stream schedules'] },
    { role: 'Contributor',     icon: FiHeart,        color: 'pink',
      description: 'Registered community members honoring loved ones.',
      permissions: ['Sign guestbooks and leave tributes','Join group buy campaigns','RSVP to memorial events','Join support groups and workshops'] },
    { role: 'Memorial Owner',  icon: FiFeather,      color: 'gold',
      description: 'The primary creator and admin of a memorial page.',
      permissions: ['Manage gallery and story content','Create and schedule memorial events','Moderate tributes and guestbook entries','Invite family members and digital heirs'] },
    { role: 'Merchant',        icon: FiShoppingBag,  color: 'green',
      description: 'Verified sellers providing memorial products and services.',
      permissions: ['Manage shop settings and branding','List products in the marketplace','Process orders and manage inventory','Withdraw earnings and view payouts'] },
    { role: 'Digital Heir',    icon: FiKey,          color: 'purple',
      description: 'Designated recipient of digital assets and legacy data.',
      permissions: ['Access private document vaults upon activation','Manage assigned digital assets','View private legacy instructions','Communicate with estate executors'] },
    { role: 'Estate Executor', icon: FiShield,       color: 'navy',
      description: 'Legal or administrative representative for estate matters.',
      permissions: ['Full access to the estate management suite','Verify and activate heir succession protocols','Coordinate with legal professionals','Manage final expense tracking and planning'] },
  ];

  const colorMap = {
    blue:   'bg-blue-50 text-blue-500',
    pink:   'bg-pink-50 text-pink-500',
    gold:   'bg-accent-gold/10 text-accent-gold',
    green:  'bg-green-50 text-green-500',
    purple: 'bg-purple-50 text-purple-500',
    navy:   'bg-accent-navy/5 text-accent-navy',
  };

  const headingMap = {
    'Support Groups':    'Community Healing',
    'Workshops':         'Guided Reflection',
    'About':             'Our Eternal Mission',
    'Legal':             'Safeguarding Legacies',
    'Legal & Will Prep': 'Your Legal Legacy',
    'Estate Services':   'Digital Legacy Protection',
    'User Roles':        'Community Roles',
    'Funeral Planning':  'Planning With Care',
    default:             'Compassionate Guidance',
  };

  return (
    <div className="pt-24 md:pt-32 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-4 sm:px-6">

        {/* Hero */}
        <section className="max-w-4xl mx-auto text-center mb-10 md:mb-16">
          <motion.div key={activeCategory} initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 bg-accent-gold/10 text-accent-gold px-4 py-1.5
                       md:px-5 md:py-2 rounded-full text-[9px] md:text-[10px] font-black uppercase
                       tracking-[0.2em] mb-6 md:mb-8 border border-accent-gold/20">
            <SafeIcon icon={
              activeCategory === 'Support Groups'   ? FiUsers
              : activeCategory === 'User Roles'     ? FiUser
              : activeCategory === 'Estate Services'? FiBriefcase
              : activeCategory === 'Legal & Will Prep'? FiBookOpen
              : FiHeart
            } />
            {activeCategory} Sanctuary
          </motion.div>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-serif text-accent-navy mb-6 md:mb-8 leading-tight">
            {headingMap[activeCategory] || headingMap.default}
          </h1>
        </section>

        {/* Tab Bar */}
        <div className="sticky top-[60px] md:top-24 z-40 bg-brand-light/90 backdrop-blur-md py-4 md:py-6
                        -mx-4 px-4 md:-mx-6 md:px-6 mb-8 md:mb-12 border-b border-brand-dark/30
                        overflow-x-auto hide-scrollbar">
          <div className="flex justify-start md:justify-center gap-2 md:gap-3 min-w-max pb-2 md:pb-0">
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => handleTabChange(cat)}
                className={`px-5 py-2.5 md:px-8 md:py-3 rounded-full text-[9px] md:text-[10px]
                            font-black uppercase tracking-widest transition-all border-2 whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-accent-navy border-accent-navy text-white'
                    : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
                }`}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Admin Toolbar */}
        {isAdmin && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4
                       bg-accent-navy/5 border-2 border-accent-navy/20 rounded-[24px] px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-accent-navy rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiSettings} className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">
                  Admin Mode Active
                </p>
                <p className="text-xs text-text-muted font-light">
                  Manage articles, videos, featured content and layout
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {showSpotlight && (
                <div className="flex items-center gap-1 bg-white border border-brand-dark rounded-xl p-1">
                  {[1, 2, 3, 4].map(n => (
                    <button key={n} onClick={() => persistGridCols(n)}
                      className={`px-3 py-1.5 rounded-lg text-[9px] font-black transition-all ${
                        gridCols === n ? 'bg-accent-navy text-white' : 'text-text-muted hover:text-accent-navy'
                      }`}>{n}</button>
                  ))}
                  <span className="text-[9px] text-text-muted font-bold uppercase tracking-widest px-1">cols</span>
                </div>
              )}
              <button onClick={() => setIsEditorOpen(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-accent-navy text-white rounded-xl
                           text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all">
                <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" />
                Open Editor
              </button>
            </div>
          </motion.div>
        )}

        {/* Content */}
        <div className="space-y-12 md:space-y-20">
          <AnimatePresence mode="wait">

            {/* Support Groups */}
            {activeCategory === 'Support Groups' ? (
              <motion.div key="groups" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6 mb-8 md:mb-12">
                  <div className="relative w-full md:max-w-md group">
                    <SafeIcon icon={FiSearch} className="absolute left-5 md:left-6 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-accent-gold w-5 h-5" />
                    <input className="w-full pl-12 md:pl-14 pr-4 md:pr-6 py-3 md:py-4 bg-white border border-brand-dark rounded-xl md:rounded-2xl outline-none focus:border-accent-gold transition-all text-sm md:text-base" placeholder="Find your community..." />
                  </div>
                  <button onClick={() => setIsCreateGroupOpen(true)} className="w-full md:w-auto btn-accent py-4 px-6 md:px-10 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest">
                    <SafeIcon icon={FiPlus} /> Start a New Group
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {groups.map(group => (
                    <SupportGroupCard key={group.id} group={group} isJoined={joinedGroups.includes(group.id)} onJoin={() => handleJoinGroup(group.id)} onLeave={handleLeaveGroup} />
                  ))}
                </div>
              </motion.div>

            ) : activeCategory === 'Workshops' ? (
              <motion.div key="workshops" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8 md:space-y-12">
                <div className="bg-accent-gold/10 p-6 md:p-10 rounded-[32px] md:rounded-[40px] border border-accent-gold/20 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8 text-center md:text-left">
                  <div className="max-w-md">
                    <h3 className="text-xl md:text-2xl font-serif text-accent-navy mb-2">Obituary Writing Tool</h3>
                    <p className="text-sm text-text-muted">Use our AI-assisted drafting tool to find the right words when they are hardest to find.</p>
                  </div>
                  <button onClick={() => setIsObituaryOpen(true)} className="btn-accent px-8 py-3 md:px-10 md:py-4 flex items-center justify-center gap-3 w-full md:w-auto">
                    <SafeIcon icon={FiZap} /> Open Writing Tool
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
                  <div className="lg:col-span-2">
                    <WorkshopsGrid workshops={mockWorkshops} onJoin={setSelectedWorkshop} onRemind={setReminderWorkshop} />
                  </div>
                  <div><WorkshopCalendar workshops={mockWorkshops} onSelectWorkshop={setSelectedWorkshop} /></div>
                </div>
              </motion.div>

            ) : activeCategory === 'Funeral Planning' ? (
              <motion.div key="planning" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <FuneralPlanning />
              </motion.div>

            ) : activeCategory === 'Legal & Will Prep' ? (
              <motion.div key="legal-will" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <LegalWillPrep />
              </motion.div>

            ) : activeCategory === 'User Roles' ? (
              <motion.div key="roles" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {userRoles.map((role, idx) => (
                  <motion.div key={role.role} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: idx * 0.1 }}
                    className="bg-white p-8 rounded-[40px] border border-brand-dark shadow-sm hover:shadow-xl transition-all group">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 ${colorMap[role.color]}`}>
                      <SafeIcon icon={role.icon} className="w-7 h-7" />
                    </div>
                    <h3 className="text-2xl font-serif text-accent-navy mb-3">{role.role}</h3>
                    <p className="text-sm text-text-muted font-light leading-relaxed mb-6">{role.description}</p>
                    <div className="space-y-3 pt-4 border-t border-brand-light">
                      <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold mb-2">Key Permissions</p>
                      {role.permissions.map((perm, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs font-medium text-accent-navy">
                          <div className="w-4 h-4 rounded-full bg-brand-light flex items-center justify-center shrink-0 mt-0.5">
                            <SafeIcon icon={FiCheckCircle} className="w-2.5 h-2.5 text-accent-gold" />
                          </div>
                          {perm}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </motion.div>

            ) : activeCategory === 'Estate Services' ? (
              <motion.div key="estate" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-12">
                <div className="bg-white rounded-[40px] border border-brand-dark shadow-sm overflow-hidden relative">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-accent-gold/5 rounded-full -mr-32 -mt-32 blur-3xl" />
                  <div className="grid lg:grid-cols-2">
                    <div className="p-8 md:p-16">
                      <div className="inline-flex items-center gap-2 bg-accent-navy/5 text-accent-navy px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-8">
                        <SafeIcon icon={FiAward} /> Premium Concierge
                      </div>
                      <h2 className="text-3xl md:text-5xl font-serif text-accent-navy mb-6">Digital Heir Retainer Service</h2>
                      <p className="text-lg text-text-muted font-light leading-relaxed mb-8">Ongoing professional management for your digital and legal assets.</p>
                      <div className="space-y-4 mb-10">
                        {['Annual Digital Asset Audit','Succession Management Support','Legal Document Vault Security','Generational Transfer Coordination','Priority Support Access'].map((f, i) => (
                          <div key={i} className="flex items-center gap-3 text-sm font-bold text-accent-navy">
                            <div className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center shrink-0">
                              <SafeIcon icon={FiCheckCircle} className="w-3 h-3" />
                            </div>
                            {f}
                          </div>
                        ))}
                      </div>
                      <a href="mailto:concierge@onefinallegacy.com?subject=Inquiry: Digital Heir Retainer Service"
                        className="btn-primary py-5 px-10 flex items-center justify-center gap-3 w-full md:w-auto">
                        <SafeIcon icon={FiMail} /> Contact Support
                      </a>
                    </div>
                    <div className="bg-brand-light relative min-h-[400px]">
                      <img src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&q=80&w=1000"
                        className="w-full h-full object-cover grayscale opacity-50" alt="Estate Services" />
                    </div>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-8">
                  {[
                    { icon: FiLock,  color: 'bg-blue-50 text-blue-500',           title: 'DocuVault™',      desc: 'Securely store wills, trusts, and deeds with multi-party authorization protocols.' },
                    { icon: FiZap,   color: 'bg-accent-gold/10 text-accent-gold', title: 'Heir Activation', desc: 'Automated protocols to transfer account ownership to designated digital heirs.' },
                    { icon: FiUsers, color: 'bg-accent-navy/5 text-accent-navy',  title: 'Family Liaison',  desc: 'Assistance in coordinating between family members and legal representatives.' },
                  ].map(c => (
                    <div key={c.title} className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm">
                      <div className={`w-12 h-12 ${c.color} rounded-2xl flex items-center justify-center mb-6`}>
                        <SafeIcon icon={c.icon} className="w-6 h-6" />
                      </div>
                      <h3 className="text-xl font-serif text-accent-navy mb-4">{c.title}</h3>
                      <p className="text-sm text-text-muted font-light leading-relaxed">{c.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

            ) : activeCategory === 'Legal' ? (
              <motion.div key="legal" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8 md:space-y-12">
                <div className="bg-accent-gold/10 border border-accent-gold/20 p-6 md:p-10 rounded-[32px] md:rounded-[40px] flex flex-col md:flex-row items-center justify-between gap-6">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-accent-gold mb-2">New Feature</p>
                    <h3 className="text-xl md:text-2xl font-serif text-accent-navy mb-2">Looking for Will & Estate Planning?</h3>
                    <p className="text-sm text-text-muted font-light">We've launched a dedicated AI Will Drafting service with legal checklists and attorney referrals.</p>
                  </div>
                  <button onClick={() => handleTabChange('Legal & Will Prep')}
                    className="btn-primary py-4 px-8 flex items-center gap-3 whitespace-nowrap w-full md:w-auto justify-center">
                    <SafeIcon icon={FiBookOpen} /> Go to Legal & Will Prep
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  <div className="bg-white p-8 md:p-10 rounded-[32px] md:rounded-[40px] border border-brand-dark shadow-sm">
                    <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center mb-6">
                      <SafeIcon icon={FiShield} className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-serif text-accent-navy mb-4">Privacy Policy</h3>
                    <p className="text-sm text-text-muted font-light">We take legacy privacy with extreme seriousness. Your data is encrypted using AES-256 standards.</p>
                  </div>
                  <div className="bg-white p-8 md:p-10 rounded-[32px] md:rounded-[40px] border border-brand-dark shadow-sm">
                    <div className="w-12 h-12 bg-accent-gold/10 text-accent-gold rounded-2xl flex items-center justify-center mb-6">
                      <SafeIcon icon={FiFileText} className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-serif text-accent-navy mb-4">Terms of Service</h3>
                    <p className="text-sm text-text-muted font-light">By using OneFinalLegacy, you agree to maintain a respectful environment and accept our platform usage guidelines.</p>
                  </div>
                </div>
              </motion.div>

            ) : activeCategory === 'About' ? (
              <motion.div key="about" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-12 md:space-y-20">
                <div className="bg-white p-8 md:p-20 rounded-[40px] md:rounded-[60px] border border-brand-dark shadow-sm">
                  <div className="max-w-3xl mx-auto text-center">
                    <SafeIcon icon={FiFeather} className="w-12 h-12 md:w-16 md:h-16 text-accent-gold mx-auto mb-6 md:mb-10" />
                    <h2 className="text-3xl md:text-5xl font-serif text-accent-navy mb-6 md:mb-8">Our Sacred Story</h2>
                    <p className="text-lg md:text-xl text-text-muted font-light px-4 mb-8 italic">
                      "OneFinalLegacy was founded on the belief that a life well-lived deserves a legacy that never fades."
                    </p>
                  </div>
                </div>
                <ContactForm />
              </motion.div>

            ) : (
              /* Default: Spotlight + Articles + Help Videos */
              <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-16">

                {showSpotlight && (
                  <SpotlightSection
                    featuredArticles={featuredArticles}
                    featuredVideos={featuredVideos}
                    loading={loadingContent}
                    onShare={openShare}
                  />
                )}

                {/* Article Grid */}
                {loadingContent ? (
                  <div className={`grid ${gridClass} gap-8 md:gap-10`}>
                    {Array.from({ length: gridCols * 2 }).map((_, i) => (
                      <div key={i} className="bg-white rounded-[32px] overflow-hidden border border-brand-dark animate-pulse">
                        <div className="h-56 bg-brand-light" />
                        <div className="p-6 md:p-10 space-y-3">
                          <div className="h-4 bg-brand-light rounded-full w-1/3" />
                          <div className="h-6 bg-brand-light rounded-full" />
                          <div className="h-4 bg-brand-light rounded-full w-2/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`grid ${gridClass} gap-8 md:gap-10`}>
                    {filteredResources.length > 0 ? filteredResources.map((res, idx) => (
                      <ArticleCard
                        key={res.id} resource={res} index={idx}
                        isAdmin={isAdmin}
                        onEdit={isAdmin ? () => setIsEditorOpen(true) : undefined}
                        onShare={openShare}
                        onView={handleArticleView}
                      />
                    )) : (
                      <div className={`${gridCols > 1 ? 'col-span-full' : ''} text-center py-24 bg-white
                                      rounded-[50px] border-2 border-dashed border-brand-dark`}>
                        <SafeIcon icon={FiSearch} className="w-16 h-16 text-text-muted/20 mx-auto mb-4" />
                        <h3 className="text-2xl font-serif text-accent-navy mb-2">No articles found</h3>
                        <p className="text-text-muted font-light">Try a different category or create a new article.</p>
                        {isAdmin && (
                          <button onClick={() => setIsEditorOpen(true)}
                            className="mt-6 px-6 py-3 bg-accent-navy text-white rounded-2xl
                                       text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all">
                            Open Editor
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Help Videos */}
                {showSpotlight && (
                  <div className="space-y-8">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiVideo} className="w-5 h-5" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-serif text-accent-navy">Help & Guidance Videos</h2>
                        <p className="text-sm text-text-muted font-light">
                          Watch our curated video guides to get the most from OneFinalLegacy.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {helpVideos.map((video, idx) => (
                        <HelpVideoCard key={video.id} video={video} index={idx} onShare={openShare} />
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>

      {/* Modals */}
      <JoinWorkshopModal isOpen={!!selectedWorkshop} onClose={() => setSelectedWorkshop(null)} workshop={selectedWorkshop} />
      {reminderWorkshop && (
        <EventReminderModal isOpen={!!reminderWorkshop} onClose={() => setReminderWorkshop(null)}
          eventTitle={reminderWorkshop.title} eventDate={reminderWorkshop.date}
          eventDescription={reminderWorkshop.description} />
      )}
      <CreateGroupModal isOpen={isCreateGroupOpen} onClose={() => setIsCreateGroupOpen(false)} onCreate={handleCreateGroup} />
      <ObituaryHelperModal isOpen={isObituaryOpen} onClose={() => setIsObituaryOpen(false)} />
      <AnimatePresence>
        {selectedGroup && <GroupDetailView group={selectedGroup} onClose={() => setSelectedGroup(null)} />}
      </AnimatePresence>
      <AnimatePresence>
        {isEditorOpen && <AdminResourceEditor isOpen={isEditorOpen} onClose={handleEditorClose} />}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {shareTarget && (
          <ShareModal isOpen={!!shareTarget} onClose={closeShare}
            title={shareTitle} description={shareDesc} url={shareUrl} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Resources;